import { maskAccountNumber } from "../../../utils/masks/accountDestination.mask";

import type {
  NachaPassthroughFields,
  PaginatedNachaPassthroughDetails,
} from "./types";

/** Maps a raw pass-through submission onto the shared camel-cased fields. */
export const mapNachaPassthroughSubmissionFields = (
  submission: API.NachaPassthrough.Submission,
): NachaPassthroughFields => ({
  paymentTitle: submission.title,
  fileName: submission.original_file_name,
  directionSummary: submission.direction_summary,
  balanceClassification: submission.balance_classification,
  paymentCreditCount: submission.payment_credit_count,
  paymentDebitCount: submission.payment_debit_count,
  creditAmount: submission.payment_total_credit_amount,
  debitAmount: submission.payment_total_debit_amount,
  secCodes: submission.sec_codes,
  companyIds: submission.company_ids,
  processingDate: submission.processing_date,
  isSameDay: submission.is_same_day,
  earliestEffectiveDate: submission.earliest_effective_date,
  latestEffectiveDate: submission.latest_effective_date,
  originatingAccountCount: submission.distinct_originating_account_count,
  batches: submission.batches,
});

/**
 * Derives the display details for a pass-through file: distinct SEC codes,
 * company IDs/names, entry descriptions, effective dates, and originating
 * accounts (masked to last 4) across its batches.
 */
export const buildNachaPassthroughDetails = (
  fields: NachaPassthroughFields,
): PaginatedNachaPassthroughDetails => {
  const getDistinctValues = (
    getValue: (batch: API.NachaPassthrough.Batch) => string | null,
  ) => [
    ...new Set(
      fields.batches
        .map(getValue)
        .filter((value): value is string => Boolean(value)),
    ),
  ];
  const accounts = new Map<
    string,
    PaginatedNachaPassthroughDetails["originatingAccounts"][number]
  >();
  fields.batches.forEach((batch) => {
    const account = {
      accountId: batch.offset_account,
      lastFour: maskAccountNumber(batch.masked_offset_account_number),
    };
    if (account.accountId || account.lastFour) {
      accounts.set(`${account.accountId ?? ""}:${account.lastFour}`, account);
    }
  });

  return {
    paymentTitle: fields.paymentTitle,
    fileName: fields.fileName,
    directionSummary: fields.directionSummary,
    balanceClassification: fields.balanceClassification,
    paymentCreditCount: fields.paymentCreditCount,
    paymentDebitCount: fields.paymentDebitCount,
    creditAmount: fields.creditAmount,
    debitAmount: fields.debitAmount,
    secCodes: getDistinctValues((batch) => batch.sec_code),
    companyIds: getDistinctValues((batch) => batch.company_id),
    processingDate: fields.processingDate,
    isSameDay: fields.isSameDay,
    earliestEffectiveDate: fields.earliestEffectiveDate,
    latestEffectiveDate: fields.latestEffectiveDate,
    originatingAccountCount: fields.originatingAccountCount,
    batches: fields.batches,
    originatingAccounts: [...accounts.values()],
    companyNames: getDistinctValues((batch) => batch.company_name),
    entryDescriptions: getDistinctValues((batch) => batch.entry_description),
    effectiveDates: getDistinctValues((batch) => batch.effective_date),
  };
};

/** File-level display details for a pass-through submission fetched directly. */
export const formatNachaPassthroughSubmissionDetails = (
  submission: API.NachaPassthrough.Submission,
): PaginatedNachaPassthroughDetails =>
  buildNachaPassthroughDetails(mapNachaPassthroughSubmissionFields(submission));
